
import type { JSX } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $insertNodeToNearestRoot, $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import { $createParagraphNode, $createRangeSelection, $getNodeFromDOMNode,$findMatchingParent, $getSelection, $insertNodes, $isNodeSelection, $isRangeSelection, $isRootOrShadowRoot, $setSelection, COMMAND_PRIORITY_EDITOR, COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_LOW, createCommand, DRAGOVER_COMMAND, DRAGSTART_COMMAND, DROP_COMMAND, getDOMSelectionFromTarget, isHTMLElement, LexicalCommand, LexicalEditor, $getNodeByKey } from 'lexical';
import { useEffect } from 'react';

import { $createEquationNode, EquationNode, isEquationNode, SerializedEquationNode } from '../nodes/EquationNode';

export const INSERT_EQUATION_COMMAND: LexicalCommand<{ svg: string, latex: string }> = createCommand(
  'INSERT_EQUATION_COMMAND',
);

export default function EquationPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([EquationNode])) {
      throw new Error('EquationPlugin: EquationNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand<{ svg: string, latex: string }>(
        INSERT_EQUATION_COMMAND,
        (payload) => {
          const equationNode = $createEquationNode(payload.svg, payload.latex);
          $insertNodes([equationNode]);
          if ($isRootOrShadowRoot(equationNode.getParentOrThrow())) {
            $wrapNodeInElement(equationNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand<DragEvent>(
        DRAGSTART_COMMAND,
        (event) => {
          return $onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH,
      ),
      // editor.registerCommand<DragEvent>(
      //   DRAGOVER_COMMAND,
      //   (event) => {
      //     return $onDragover(event);
      //   },
      //   COMMAND_PRIORITY_LOW,
      // ),
      editor.registerCommand<DragEvent>(
        DROP_COMMAND,
        (event) => {
          return $onDrop(event, editor);
        },
        COMMAND_PRIORITY_HIGH,
      ))
  }, [editor]);

  return null;
}

function $onDragStart(event: DragEvent): boolean {
  const node = $getEquationNode(event.target);
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  dataTransfer.setData('text/plain', '_');
  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({
      data: {
        svg: node.__svg,
        latex: node.__latex
      },
      type: 'equation',
      key: node.__key
    }),
  );

  return true;
}

function $onDragover(event: DragEvent): boolean {
  const node = $getEquationNode(event.target);
  if (!node) {
    return false;
  }
  if (!canDropEquation(event)) {
    event.preventDefault();
  }
  return true;
}

function $onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const data = getDragEquationData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();
  if (canDropEquation(event)) {
    const range = getDragSelection(event);
    const node = $getNodeByKey(data.key);
    if(!node) {
      return false;
    }
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);
    editor.dispatchCommand(INSERT_EQUATION_COMMAND, data.data);
  }
  return true;
}

function $getEquationNode(target: EventTarget | null): EquationNode | null {
  if(target instanceof HTMLImageElement && target.classList.contains("equation")) {
    const node = $getNodeFromDOMNode(target);
    if(node && isEquationNode(node)) {
      return node
    }
  }
  return null;
}

function getDragEquationData(event: DragEvent): null | {data: {svg: string, latex: string}, type: 'equation', key: string} {
  const dragData = event.dataTransfer?.getData('application/x-lexical-drag');
  if (!dragData) {
    return null;
  }
  const data = JSON.parse(dragData);
  if (data.type !== 'equation') {
    return null;
  }

  return data;
}

function canDropEquation(event: DragEvent): boolean {
  const target = event.target;
  return !!(
    isHTMLElement(target) &&
    // !target.closest('code, span.editor-image') &&
    isHTMLElement(target.parentElement) &&
    target.parentElement.closest('div.editor-input')
  );
}

function getDragSelection(event: DragEvent): Range | null | undefined {
  let range;
  const domSelection = getDOMSelectionFromTarget(event.target);
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
    range = domSelection.getRangeAt(0);
  } else {
    throw Error(`Cannot get the selection when dragging`);
  }

  return range;
}

declare global {
  interface DragEvent {
    rangeOffset?: number;
    rangeParent?: Node;
  }
}